import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { LogTimeCell } from "../../cells/LogTimeCell";
import { HistoryDetailsEntityCell } from "../../cells/HistoryDetailsEntityCell";
import { HistoryDocumentFileCell } from "../cells/HistoryDocumentFileCell";
import { HistoryDocumentFinancialAccountCell } from "../cells/HistoryDocumentFinancialAccountCell";
import {
    HistoryDocumentModifiedValueCell,
    HistoryDocumentPreviousValueCell,
} from "../cells/HistoryDocumentDiffValueCell";
import {
    HistoryDocumentCreateDeleteModifiedCell,
    HistoryDocumentCreateDeletePreviousCell,
} from "../cells/HistoryDocumentCreateDeleteValueCell";
import {
    HistoryDocumentCreateDeleteDetailsRow,
    HistoryDocumentUpdateDetailsRow,
} from "../utils/historyDocumentDetailsRow";
import { HistoryDocumentDownloadDetailsRow } from "../utils/historyDocumentDownloadRows";
import {
    HistoryDocumentDetailsColumn,
    HistoryDocumentDownloadColumn,
} from "./historyDocumentDetailsView";

const DEFAULT_PREVIOUS_LABEL = "Prior value";
const DEFAULT_MODIFIED_LABEL = "Updated value";

const getSingleFieldLabel = (
    data: HistoryDocumentUpdateDetailsRow[],
): string | null => {
    const fieldValues = data
        .map((row) => row.changedFieldLabel)
        .filter((field): field is string => field !== null);
    const uniqueFields = Array.from(new Set(fieldValues));
    if (uniqueFields.length !== 1) {
        return null;
    }
    return uniqueFields[0];
};

export const createHistoryDocumentCreateDeleteViewConfig = (): ViewConfig<
    HistoryDocumentCreateDeleteDetailsRow,
    HistoryDocumentDetailsColumn,
    never
> => ({
    columns: {
        [HistoryDocumentDetailsColumn.TIME]: {
            name: "Time",
            header: "",
            accessorKey: "logDate",
            cell: LogTimeCell,
            ...DATE_COLUMN_SIZE,
        },
        [HistoryDocumentDetailsColumn.DOCUMENT_NAME]: {
            name: "Document",
            header: "Document",
            accessorKey: "fileName",
            cell: HistoryDocumentFileCell,
            minSize: 260,
            maxSize: 400,
        },
        [HistoryDocumentDetailsColumn.ENTITY]: {
            name: "Entity",
            header: "Entity",
            accessorKey: "entityId",
            cell: HistoryDetailsEntityCell,
            minSize: 80,
            maxSize: 160,
        },
        [HistoryDocumentDetailsColumn.ACCOUNT]: {
            name: "Account",
            header: "Account",
            accessorKey: "financialAccountId",
            cell: HistoryDocumentFinancialAccountCell,
            minSize: 200,
            maxSize: 320,
        },
        [HistoryDocumentDetailsColumn.PREVIOUS]: {
            name: "Previous",
            header: "Previous",
            accessorKey: "previous",
            cell: HistoryDocumentCreateDeletePreviousCell,
            minSize: 280,
            maxSize: 420,
        },
        [HistoryDocumentDetailsColumn.MODIFIED]: {
            name: "Modified",
            header: "Modified",
            accessorKey: "modified",
            cell: HistoryDocumentCreateDeleteModifiedCell,
            minSize: 280,
            maxSize: 420,
        },
    },
});

export const createHistoryDocumentDownloadViewConfig = (): ViewConfig<
    HistoryDocumentDownloadDetailsRow,
    HistoryDocumentDownloadColumn,
    never
> => ({
    columns: {
        [HistoryDocumentDetailsColumn.TIME]: {
            name: "Time",
            header: "",
            accessorKey: "logDate",
            cell: LogTimeCell,
            ...DATE_COLUMN_SIZE,
        },
        [HistoryDocumentDetailsColumn.DOCUMENT_NAME]: {
            name: "Document",
            header: "Downloaded document",
            accessorKey: "fileName",
            cell: HistoryDocumentFileCell,
            minSize: 260,
            maxSize: 560,
        },
        [HistoryDocumentDetailsColumn.ENTITY]: {
            name: "Entity",
            header: "Entity",
            accessorKey: "entityId",
            cell: HistoryDetailsEntityCell,
            minSize: 80,
            maxSize: 160,
        },
    },
});

export const createHistoryDocumentUpdateViewConfig = (
    data: HistoryDocumentUpdateDetailsRow[],
): ViewConfig<
    HistoryDocumentUpdateDetailsRow,
    HistoryDocumentDetailsColumn,
    never
> => {
    const singleField = getSingleFieldLabel(data);
    const previousLabel = singleField
        ? `Prior ${singleField.toLowerCase()}`
        : DEFAULT_PREVIOUS_LABEL;
    const modifiedLabel = singleField
        ? `Updated ${singleField.toLowerCase()}`
        : DEFAULT_MODIFIED_LABEL;

    return {
        columns: {
            [HistoryDocumentDetailsColumn.TIME]: {
                name: "Time",
                header: "",
                accessorKey: "logDate",
                cell: LogTimeCell,
                ...DATE_COLUMN_SIZE,
            },
            [HistoryDocumentDetailsColumn.DOCUMENT_NAME]: {
                name: "Document",
                header: "Document",
                accessorKey: "fileName",
                cell: HistoryDocumentFileCell,
                minSize: 260,
                maxSize: 400,
            },
            [HistoryDocumentDetailsColumn.ENTITY]: {
                name: "Entity",
                header: "Entity",
                accessorKey: "entityId",
                cell: HistoryDetailsEntityCell,
                minSize: 80,
                maxSize: 160,
            },
            [HistoryDocumentDetailsColumn.ACCOUNT]: {
                name: "Account",
                header: "Account",
                accessorKey: "financialAccountId",
                cell: HistoryDocumentFinancialAccountCell,
                minSize: 200,
                maxSize: 320,
            },
            [HistoryDocumentDetailsColumn.PREVIOUS]: {
                name: previousLabel,
                header: previousLabel,
                accessorKey: "previous",
                cell: HistoryDocumentPreviousValueCell,
                minSize: 280,
                maxSize: 420,
            },
            [HistoryDocumentDetailsColumn.MODIFIED]: {
                name: modifiedLabel,
                header: modifiedLabel,
                accessorKey: "modified",
                cell: HistoryDocumentModifiedValueCell,
                minSize: 280,
                maxSize: 420,
            },
        },
    };
};
